import { createWalletClient, custom, getAddress, numberToHex } from "viem";
import type { Account, Chain, Hex, Transport, WalletClient } from "viem";
import { addChainParams, robinhoodChain } from "./chain";
import { WalletMissingError, isUnrecognisedChain, isUserRejection } from "./errors";
import type { Eip1193Provider } from "./global";

export const METAMASK_INSTALL_URL = "https://metamask.io/download/";

/** Picks MetaMask when several wallets share window.ethereum. */
export function getEthereum(): Eip1193Provider | null {
  if (typeof window === "undefined") return null;
  const injected = window.ethereum;
  if (!injected) return null;
  if (Array.isArray(injected.providers)) {
    return injected.providers.find((p) => p.isMetaMask) ?? injected.providers[0] ?? injected;
  }
  return injected;
}

export const hasEthereum = (): boolean => getEthereum() !== null;

function requireEthereum(): Eip1193Provider {
  const provider = getEthereum();
  if (!provider) throw new WalletMissingError("metamask");
  return provider;
}

/** eth_requestAccounts. Hands back the checksummed active address. */
export async function connectEvm(): Promise<string> {
  const provider = requireEthereum();
  const accounts = await provider.request<string[]>({ method: "eth_requestAccounts" });
  const first = accounts?.[0];
  if (!first) throw new Error("MetaMask did not return an account.");
  return getAddress(first);
}

/** Re-opens the MetaMask account picker so the user can register a different address. */
export async function requestEvmAccountChange(): Promise<string> {
  const provider = requireEthereum();
  try {
    await provider.request({
      method: "wallet_requestPermissions",
      params: [{ eth_accounts: {} }],
    });
  } catch (error) {
    // Wallets that do not implement wallet_requestPermissions still let the
    // user switch accounts by hand, so fall through to eth_requestAccounts.
    if (isUserRejection(error)) throw error;
  }
  return connectEvm();
}

/** Reads the already-authorised address without prompting, or null. */
export async function readConnectedEvm(): Promise<string | null> {
  const provider = getEthereum();
  if (!provider) return null;
  try {
    const accounts = await provider.request<string[]>({ method: "eth_accounts" });
    const first = accounts?.[0];
    return first ? getAddress(first) : null;
  } catch {
    return null;
  }
}

/**
 * wallet_switchEthereumChain, falling back to wallet_addEthereumChain when the
 * wallet does not know Robinhood Chain yet.
 */
export async function ensureRobinhoodChain(
  chainId: number,
  rpcUrl: string,
  explorerUrl: string,
): Promise<void> {
  const provider = requireEthereum();
  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: numberToHex(chainId) }],
    });
  } catch (error) {
    if (!isUnrecognisedChain(error)) throw error;
    await provider.request({
      method: "wallet_addEthereumChain",
      params: [addChainParams(chainId, rpcUrl, explorerUrl)],
    });
  }
}

/** personal_sign of the fixed template, through viem. */
export async function personalSignEvm(message: string, address: string): Promise<Hex> {
  const provider = requireEthereum();
  const client = createWalletClient({ transport: custom(provider) });
  return client.signMessage({ account: getAddress(address) as Hex, message });
}

export function walletClientFor(
  address: string,
  chainId: number,
  rpcUrl: string,
  explorerUrl: string,
): WalletClient<Transport, Chain, Account> {
  const provider = requireEthereum();
  return createWalletClient({
    account: getAddress(address) as Hex,
    chain: robinhoodChain(rpcUrl, explorerUrl, chainId),
    transport: custom(provider),
  });
}
